"use client";

// Cache invalidation trigger

import React, { createContext, useContext } from 'react';

// Using a partial match of ISettings to avoid needing full mongoose Document
export interface IGlobalSettings {
  siteName: string;
  seoTitle: string;
  seoDescription: string;
  contactEmail: string;
  contactPhone: string;
  companyLogo: string;
  address: string;
  websiteUrl: string;
  googleMapsUrl: string;
  businessHours: string;
  copyrightText: string;
  companyDescription: string;
  contactUsInformation: string;
  tagline: string;
  supportContactDetails: string;
  privacyPolicy: string;
  termsConditions: string;
  socialLinks: {
    facebook: string;
    instagram: string;
    linkedin: string;
    twitter: string;
    youtube: string;
    whatsapp: string;
  };
  footerLinks: Array<{ label: string; url: string }>;
  externalLinks: Array<{ label: string; url: string }>;
}

const defaultSettings: IGlobalSettings = {
  siteName: 'Compare All Consultancy',
  seoTitle: 'Compare All - Independent Procurement Strategy',
  seoDescription: 'Unbiased and independent service to help you determine the best products and services for your business.',
  contactEmail: 'info@compareall.ltd.uk',
  contactPhone: '0800 170 0216',
  companyLogo: '',
  address: "Devon House, 58 St Katharine's Way, London E1W 1JP",
  websiteUrl: 'https://www.compareall.ltd.uk',
  googleMapsUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2483.250493867057!2d-0.0734005!3d51.5086161!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760334812f8623%3A0xc34a5d8b7a0f7e4b!2sDevon%20House%2C%2058%20St%20Katharine&#39;s%20Way%2C%20London%20E1W%201JP!5e0!3m2!1sen!2suk!4v1700000000000!5m2!1sen!2suk',
  businessHours: 'Mon - Fri: 9:00 AM - 5:00 PM',
  copyrightText: '© 2026 Compare All Consultancy. All Rights Reserved.',
  companyDescription: 'A premium independent sourcing & commercial advisory service tailored for modern enterprises.',
  contactUsInformation: `Compare All Ltd are delighted to quote on the sourcing and procurement of all your office needs.  Please use our <a href="/quote">'Quote Me! form.'</a>`,
  tagline: 'Premium Independent Sourcing & Sourcing Advisory',
  supportContactDetails: 'support@compareall.ltd.uk',
  privacyPolicy: '',
  termsConditions: '',
  socialLinks: {
    facebook: '',
    instagram: '',
    linkedin: 'https://www.linkedin.com/company/compare-all-consultancy/',
    twitter: '',
    youtube: '',
    whatsapp: '',
  },
  footerLinks: [],
  externalLinks: [],
};

const SettingsContext = createContext<IGlobalSettings>(defaultSettings);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    return defaultSettings;
  }
  return context;
};

export const SettingsProvider = ({
  children,
  initialSettings,
}: {
  children: React.ReactNode;
  initialSettings?: Partial<IGlobalSettings> | null;
}) => {
  // Merge initialSettings with defaultSettings in case some fields are missing from DB
  const mergedSettings = { ...defaultSettings, ...initialSettings };

  if (initialSettings?.socialLinks) {
    mergedSettings.socialLinks = { ...defaultSettings.socialLinks, ...initialSettings.socialLinks };
  }

  return (
    <SettingsContext.Provider value={mergedSettings}>
      {children}
    </SettingsContext.Provider>
  );
};
